export const PET_CHARACTERS = {
  trex: { id: "trex", name: "T-Rex", personality: "bold", base: 68, code: "TRX" },
  triceratops: { id: "triceratops", name: "Triceratops", personality: "steady", base: 62, code: "TRI" },
  velociraptor: { id: "velociraptor", name: "Velociraptor", personality: "witty", base: 64, code: "VEL" },
  spinosaurus: { id: "spinosaurus", name: "Spinosaurus", personality: "dramatic", base: 66, code: "SPI" },
  chicken: { id: "chicken", name: "Chicken", personality: "chaotic", base: 59, code: "CHK" },
};

export const RARITIES = {
  gold: {
    label: "Gold",
    weight: 8,
    bonus: 44,
    color: "#FFBF35",
    gradient: "linear-gradient(180deg, #FFBF35 0%, #FFE992 100%)",
  },
  silver: {
    label: "Silver",
    weight: 17,
    bonus: 32,
    color: "#D3D3D3",
    gradient: "linear-gradient(180deg, #D3D3D3 0%, #8B8B8B 100%)",
  },
  ruby: {
    label: "Ruby",
    weight: 30,
    bonus: 22,
    color: "#FF5335",
    gradient: "linear-gradient(180deg, #FF5335 0%, #800411 100%)",
  },
  emerald: {
    label: "Emerald",
    weight: 45,
    bonus: 12,
    color: "#71FF6A",
    gradient: "linear-gradient(180deg, #71FF6A 0%, #058433 100%)",
  },
};

const CARD_VARIANTS_PER_CHARACTER = 12;

const TRAITS = [
  "witty",
  "funny",
  "curious",
  "brave",
  "dramatic",
  "gentle",
  "chaotic",
  "loyal",
  "clever",
  "stubborn",
  "swift",
  "watchful",
];

const CHARACTER_LINES = {
  trex: [
    "Small arms, giant authority.",
    "The page shakes when this one approves.",
    "A tiny crown fits surprisingly well.",
    "Built for bold clicks and bigger entrances.",
    "Roars first, debugs later.",
    "Carries prehistoric confidence in every step.",
    "A collector's pull with serious bite.",
    "Looks rare even when standing still.",
    "Protects the portfolio like a kingdom.",
    "Every stomp counts as a feature.",
    "A loud little guardian with taste.",
    "This one was born for dramatic reveals.",
  ],
  triceratops: [
    "Steady feet, steady heart.",
    "Built like a calm little fortress.",
    "Trustworthy enough to guard the footer.",
    "A gentle pull with serious balance.",
    "Patient, loyal, and hard to distract.",
    "Keeps every layout grounded.",
    "A quiet tank with excellent manners.",
    "Slow steps, strong decisions.",
    "This card prefers stable platforms.",
    "Protects good work without making noise.",
    "Kind eyes, sturdy stats.",
    "A rare kind of calm.",
  ],
  velociraptor: [
    "Fast thoughts, faster feet.",
    "Already found three hidden details.",
    "A clever pull with sharp timing.",
    "Reads the pixels before they blink.",
    "Small frame, suspiciously large brain.",
    "Plans routes through every section.",
    "Quick hops, clean exits.",
    "This one knows where the bugs hide.",
    "A strategist disguised as a site pet.",
    "Very polite, very dangerous to bad UX.",
    "Calculates before it chirps.",
    "Speed with a tiny grin.",
  ],
  spinosaurus: [
    "Arrives like the page needs a soundtrack.",
    "A dramatic pull with cinematic posture.",
    "Every patrol deserves lighting.",
    "Turns scrolling into a scene change.",
    "Tall presence, deeper lore.",
    "Guards the site with flair.",
    "Makes even idle animation feel epic.",
    "A card with main character weather.",
    "Walks like a final reveal.",
    "This one collects atmosphere.",
    "Every click gets a flourish.",
    "Built for grand entrances.",
  ],
  chicken: [
    "Tiny chaos, legally collectible.",
    "Pecks bugs with unreasonable confidence.",
    "A loud pull in a small package.",
    "Runs the page like it owns crumbs.",
    "Officially unserious, secretly powerful.",
    "Panic hops are part of the charm.",
    "Small feet, massive opinions.",
    "This one found a crumb of JavaScript.",
    "Unstable in the best possible way.",
    "A collectible alarm system.",
    "Looks common. Acts legendary.",
    "The chaos has selected you.",
  ],
};

const RARITY_SENTENCE_PARTS = {
  gold: "A gold-border pull with peak aura",
  silver: "A silver-border pull with polished nerve",
  ruby: "A ruby-border pull with dangerous sparkle",
  emerald: "An emerald-border pull with fresh energy",
};

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (items) => items[Math.floor(Math.random() * items.length)];

const weightedPick = (items) => {
  const total = items.reduce((sum, item) => sum + item.weight, 0);
  let roll = Math.random() * total;

  for (const item of items) {
    roll -= item.weight;
    if (roll <= 0) return item;
  }

  return items[0];
};

const cardImageContext = require.context(
  "./assets/cards",
  true,
  /\.(png|jpe?g|webp)$/
);

const packetImageContext = require.context(
  "./assets/card-packets",
  true,
  /\.(png|jpe?g|webp)$/
);

const cardImageKeys = cardImageContext.keys();
const packetImageKeys = packetImageContext.keys();

const findImage = (context, keys, pathWithoutExt) => {
  const found = keys.find(
    (key) =>
      key === `${pathWithoutExt}.png` ||
      key === `${pathWithoutExt}.jpg` ||
      key === `${pathWithoutExt}.jpeg` ||
      key === `${pathWithoutExt}.webp`
  );

  return found ? context(found) : "";
};

export const getCharacterProfile = (id) =>
  PET_CHARACTERS[id] || PET_CHARACTERS.trex;

export const resolveCardImage = (imagePath) => {
  if (!imagePath) return "";
  const cleanPath = imagePath.replace(/\.(png|jpe?g|webp)$/i, "");
  return findImage(cardImageContext, cardImageKeys, `./${cleanPath}`);
};

export const getPacketFrames = (characterId) => {
  const character = getCharacterProfile(characterId);

  return [1, 2, 3, 4, 5]
    .map((num) =>
      findImage(packetImageContext, packetImageKeys, `./${character.id}/packet${num}`)
    )
    .filter(Boolean);
};

const getCardImageOptions = (characterId) => {
  const base = `./${characterId}/`;

  return cardImageKeys
    .filter((key) => key.startsWith(base))
    .sort((a, b) => {
      const numA = Number(a.match(/card(\d+)/i)?.[1] || 0);
      const numB = Number(b.match(/card(\d+)/i)?.[1] || 0);
      return numA - numB;
    })
    .map((key) => ({
      src: cardImageContext(key),
      path: key.replace("./", ""),
      variant: Number(key.match(/card(\d+)/i)?.[1] || 1),
    }));
};

const makeFlavorLine = (characterId, rarityKey, variant) => {
  const lines = CHARACTER_LINES[characterId] || CHARACTER_LINES.trex;
  const baseLine = lines[(variant - 1) % lines.length];
  return `${RARITY_SENTENCE_PARTS[rarityKey]}. ${baseLine}`;
};

export function createPetCard(characterId, signature = "") {
  const character = getCharacterProfile(characterId);

  const rarityKey = weightedPick(
    Object.entries(RARITIES).map(([id, value]) => ({ id, ...value }))
  ).id;

  const rarity = RARITIES[rarityKey];
  const imageOptions = getCardImageOptions(character.id);
  const fallbackVariant = rand(1, CARD_VARIANTS_PER_CHARACTER);

  const image = imageOptions.length
    ? pick(imageOptions)
    : {
        src: "",
        path: `${character.id}/card${fallbackVariant}.png`,
        variant: fallbackVariant,
      };

  const variant = image.variant || fallbackVariant;
  const traits = [...TRAITS].sort(() => Math.random() - 0.5).slice(0, 3);

  return {
    id: crypto.randomUUID(),
    characterId: character.id,
    characterName: character.name,
    personality: character.personality,
    rarity: rarityKey,
    rarityLabel: rarity.label,
    rarityColor: rarity.color,
    rarityGradient: rarity.gradient,
    cardNumber: rand(1, 999),
    cardCode: `${character.code}-${rarityKey.toUpperCase()}-${String(variant).padStart(2, "0")}`,
    cardVariant: variant,
    cardImagePath: image.path,
    cardImageSrc: image.src,
    signature,
    flavorLine: makeFlavorLine(character.id, rarityKey, variant),
    traits,
    stats: {
      wit: rand(35, 99),
      charm: rand(35, 99),
      mischief: rand(35, 99),
      stamina: rand(35, 99),
    },
    basePower: character.base + rarity.bonus + rand(1, 20),
    createdAt: new Date().toISOString(),
  };
}
